import React, { useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import clsx from 'clsx'
import { useFormattingContext } from '../../FormattingProvider/FormattingContext'
import Skeleton from '../../../atoms/Skeleton'
import Tooltip from '../../../atoms/Tooltip'
import RelativeLink from '../../../atoms/RelativeLink'
import MetricWrapper from './MetricWrapper'

const useStyles = makeStyles(() => ({
  metricDisplay: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&:hover': {
      borderRadius: 'var(--border-radius-sm)',
      backgroundColor: '#F5F6F7'
    },
    '& .__header': {
      fontSize: '0.875rem',
      fontWeight: 'bold'
    },
    '& .__title-container': {
      display: 'flex',
      flexDirection: 'row'
    },
    '& .__title': {
      padding: '1px',
      color: 'var(--color-primary-400)'
    },
    '& .__value': {
      fontSize: ({ valueSize }) => valueSize || '3.5rem'
    },
    '& .__description': {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem'
    }
  },
  clickable: {
    cursor: 'pointer'
  },
  link: {
    textDecoration: 'none !important',
    '& > *': {
      textDecoration: 'none !important'
    }
  }
}))

const getValueWithSymbol = ({
  value,
  valueFormatted,
  neutralColor,
  positiveColor,
  negativeColor
}) => {
  let valueColor = neutralColor
  let prefixSymbol = ''

  if (value !== 0) {
    const isValueGreaterThanZero = value > 0
    valueColor = isValueGreaterThanZero ? positiveColor : negativeColor
    prefixSymbol = isValueGreaterThanZero ? '+' : ''
  }
  return [`${prefixSymbol}${valueFormatted}`, valueColor]
}

const Metric = ({
  title,
  value,
  header,
  isLoading,
  valueFormat,
  valueSize,
  valueColor,
  description,
  descriptionIcon,
  includeSymbol,
  tooltipTitle,
  redirectTo,

  // NEW: allows parent components to pass inline style overrides into Metric
  componentStyles
}) => {
  const classes = useStyles({ valueSize })
  const { formatter } = useFormattingContext()

  const formattedValue = useMemo(
    () => {
      if (valueFormat === 'human') {
        let formatPass = '0.0a'
        if (value >= 100000000 || (value >= 20000 && value < 1000000) || value < 1000) {
          formatPass = '0a'
        }
        return formatter(value, formatPass)
      }
      return formatter(value, valueFormat)
    },
    [formatter, value, valueFormat]
  )

  const [valueFormatted, _valueColor] = useMemo(() => {
    if (!includeSymbol) return [formattedValue, 'var(--text-color-main)']

    return getValueWithSymbol({
      value,
      valueFormatted: formattedValue,
      neutralColor: 'var(--color-neutral-700)',
      positiveColor: 'var(--color-growth-500)',
      negativeColor: 'var(--color-decline-500)'
    })
  }, [
    value,
    includeSymbol,
    formattedValue
  ])

  const metric = useMemo(() => {
    return (
      <>
        {header && (
          <div
            className='__header'
            // NEW: optional inline style override for the header element
            style={componentStyles?.header}
          >
            {header}
          </div>
        )}
        <div
          className='__title-container'
          // NEW: optional inline style override for the title/value container
          style={componentStyles?.titleContainer}
        >
          {isLoading ? (
            <Skeleton height={valueSize} width='100%' />
          ) : (
            <div
              className='__value'
              style={{
                color: valueColor || _valueColor,

                // NEW: optional inline style override for the value element
                // Placed after color so parent overrides can intentionally override color if needed
                ...componentStyles?.value
              }}
            >
              {valueFormatted}
            </div>
          )}
          {title && (
            <div
              className='__title'
              // NEW: optional inline style override for the title element
              style={componentStyles?.title}
            >
              {title}
            </div>
          )}
        </div>
        {description && (
          <div
            className='__description'
            // NEW: optional inline style override for the description wrapper
            style={componentStyles?.description}
          >
            {descriptionIcon}
            <span>{description}</span>
          </div>
        )}
      </>
    )
  }, [
    _valueColor,
    title,
    header,
    valueSize,
    isLoading,
    valueColor,
    description,
    descriptionIcon,
    valueFormatted,

    // NEW: included because componentStyles is used inside this memo
    componentStyles
  ])

  const classNames = useMemo(() => {
    return clsx(
      classes.link,
      classes.metricDisplay,
      { [classes.clickable]: Boolean(redirectTo) }
    )
  }, [classes.clickable, classes.link, classes.metricDisplay, redirectTo])

  return (
    <Tooltip title={tooltipTitle}>
      <RelativeLink
        className={classNames}
        // NEW: optional inline style override for the outer Metric link/wrapper
        style={componentStyles?.metricDisplay}
        to={redirectTo}
      >
        {metric}
      </RelativeLink>
    </Tooltip>
  )
}

Metric.propTypes = {
  header: PropTypes.string,
  title: PropTypes.string,
  value: PropTypes.number,
  isLoading: PropTypes.bool,
  valueColor: PropTypes.string,
  valueFormat: PropTypes.string,
  description: PropTypes.string,
  descriptionIcon: PropTypes.node,
  valueSize: PropTypes.string,
  includeSymbol: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  redirectTo: PropTypes.string,

  // NEW: supported style override slots for Metric markup
  componentStyles: PropTypes.shape({
    metricDisplay: PropTypes.object,
    header: PropTypes.object,
    titleContainer: PropTypes.object,
    title: PropTypes.object,
    value: PropTypes.object,
    description: PropTypes.object
  })
}

Metric.defaultProps = {
  header: undefined,
  valueSize: '3rem',
  valueColor: undefined,
  descriptionIcon: undefined,
  includeSymbol: false,
  isLoading: false,
  tooltipTitle: undefined,
  redirectTo: undefined,

  // NEW: no overrides by default
  componentStyles: undefined
}

Metric.Wrapper = MetricWrapper

export default Metric
